// Translates backend error responses without making the backend locale-aware.
//
// The backend always emits English text (`error.message`) alongside a stable
// machine `code` (e.g. 'NOT_FOUND', 'VALIDATION_ERROR' — see
// backend/src/middleware/errorHandler.ts and the routes' c.json(...) calls).
// resolveApiMessage() looks up that code in the errors:* locale namespace and
// returns the translation; if the code is missing or has no catalog entry, it
// falls back to the raw English message so the UI never shows a blank string.

import i18n from './i18n';

interface ApiErrorLike {
    error?: { code?: string; message?: string };
    message?: string;
    status?: number;
}

function normalizeRawApiMessage(raw: string, status?: number): string {
    const message = String(raw || '').trim();
    const lower = message.toLowerCase();
    if (!message) return '';

    if (lower.includes('duplicate key') || lower.includes('unique constraint') || lower.includes('already exists') || status === 409) {
        if (lower.includes('cannot be changed after account creation') || lower.includes('cannot be changed after creation')) {
            return 'This email is locked after creation. Delete this record and create a new one with the correct email.';
        }
        if (lower.includes('email')) return 'This email address is already in use.';
        if (lower.includes('mobile') || lower.includes('phone')) return 'This mobile number is already in use.';
        if (lower.includes('gr_number') || lower.includes('gr number')) return 'This GR number already exists.';
        if (lower.includes('receipt')) return 'This receipt number already exists.';
        if (lower.includes('pc') || lower.includes('device') || lower.includes('mac')) return 'This device is already registered.';
        if (lower.includes('paper') || lower.includes('title') || lower.includes('name')) return 'An item with the same name already exists.';
        return 'This record already exists. Please use a different value.';
    }

    if (lower.includes('violates foreign key constraint') || lower.includes('foreign key constraint')) {
        return 'This action could not be completed because the related record was not found or is already linked elsewhere.';
    }

    if (lower.includes('null value in column') || lower.includes('not-null constraint')) {
        return 'Please fill in all required fields before submitting.';
    }

    if (lower.includes('invalid input syntax') || lower.includes('syntax error')) {
        return 'Some entered values are invalid. Please check the form and try again.';
    }

    if (lower.includes('network error') || lower.includes('failed to fetch')) {
        return 'Could not connect to the server. Please check your connection and try again.';
    }

    if (lower.includes('permission denied') || lower.includes('forbidden')) {
        return 'You do not have permission to perform this action.';
    }

    if (lower.startsWith('[') && lower.includes('"code"') && lower.includes('"message"')) {
        try {
            const issues = JSON.parse(message);
            if (Array.isArray(issues)) {
                const issueText = issues
                    .map((issue) => String(issue?.message || '').trim())
                    .filter(Boolean)
                    .join(' ');
                if (issueText) return issueText;
            }
        } catch {
            return 'Please check the entered details and try again.';
        }
    }

    return message;
}

export function resolveApiMessage(err: unknown, fallback = 'Something went wrong'): string {
    const e = err as ApiErrorLike | null | undefined;
    const code = e?.error?.code;
    const rawMessage = e?.error?.message || e?.message;

    if (code === 'EMAIL_IMMUTABLE') {
        return 'This email is locked after creation. Delete this record and create a new one with the correct email.';
    }
    if (code && i18n.exists(`errors:${code}`)) {
        return i18n.t(`errors:${code}`);
    }
    return normalizeRawApiMessage(rawMessage || '', e?.status) || fallback;
}

export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
    const e = err as any;
    return resolveApiMessage(e?.response?.data || e?.error || e, fallback);
}
